// src/components/MegaMenu/MegaMenu.tsx
// Full-width curtain dropdown — slides down from header like Apple.com / Adobe.com

import React, { useState, useEffect, useRef } from "react";
import { ChevronDownRegular } from "@fluentui/react-icons";

interface Product {
  id: string;
  name: string;
  description: string;
  type: string;
  is_active: boolean;
}

const PRODUCT_META: Record<string, { icon: string; color: string; bg: string }> = {
  "Hospitality Software":       { icon: "🏨", color: "#92400E", bg: "#FEF3C7" },
  "CRM Software":               { icon: "🤝", color: "#1E40AF", bg: "#DBEAFE" },
  "Email Services":             { icon: "✉️",  color: "#065F46", bg: "#D1FAE5" },
  "Swift Grid":                 { icon: "⚡",  color: "#5B21B6", bg: "#EDE9FE" },
  "Accounting":                 { icon: "📊",  color: "#0E7490", bg: "#CFFAFE" },
  "Chat Channel":               { icon: "💬",  color: "#9D174D", bg: "#FCE7F3" },
  "Call App":                   { icon: "📞",  color: "#1E3A5F", bg: "#DBEAFE" },
  "Agentic Customer Services":  { icon: "🤖",  color: "#78350F", bg: "#FEF3C7" },
  "API Access (Pay-As-You-Go)": { icon: "🔌",  color: "#1E40AF", bg: "#EFF6FF" },
  "Main SaaS Platform":         { icon: "🏢",  color: "#1E293B", bg: "#F1F5F9" },
  "White Label Add-on":         { icon: "🎨",  color: "#4C1D95", bg: "#F3E8FF" },
};
const DEFAULT_META = { icon: "📦", color: "#292929", bg: "#F5F5F5" };

const COLUMNS = [
  {
    label: "Business Tools",
    desc:  "Run your operations end-to-end",
    filter: (_: Product, i: number) => i < 4,
  },
  {
    label: "Communication",
    desc:  "Connect your team and customers",
    filter: (_: Product, i: number) => i >= 4 && i < 7,
  },
  {
    label: "Intelligence",
    desc:  "AI-powered tools for every industry",
    filter: (_: Product, i: number) => i >= 7,
  },
];

interface MegaMenuProps {
  products: Product[];
  onProductClick: (product: Product) => void;
  onViewAll: () => void;
}

const MegaMenu: React.FC<MegaMenuProps> = ({ products, onProductClick, onViewAll }) => {
  const [open, setOpen]       = useState(false);
  const ref                   = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      {/* ── Inject keyframe animation once ── */}
      <style>{`
        @keyframes curtainDown {
          from { opacity: 0; transform: translateY(-16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes curtainUp {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(-16px); }
        }
        .cpn-mega-overlay {
          position: fixed;
          inset: 0;
          z-index: 8888;
          background: rgba(0,0,0,0.08);
          backdrop-filter: blur(1px);
          animation: curtainDown 0.22s ease forwards;
        }
        .cpn-mega-curtain {
          position: fixed;
          top: 48px;
          left: 0;
          right: 0;
          z-index: 8889;
          background: #ffffff;
          box-shadow: 0 12px 40px rgba(0,0,0,0.15);
          animation: curtainDown 0.25s cubic-bezier(0.16,1,0.3,1) forwards;
          border-top: 2px solid #1473e6;
          overflow: hidden;
        }
        .cpn-mega-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 36px 48px 32px;
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 0;
        }
        .cpn-col-divider {
          width: 1px;
          flex-shrink: 0;
          background: linear-gradient(to bottom, transparent, #e1e1e1 20%, #e1e1e1 80%, transparent);
          margin: 0 28px;
          align-self: stretch;
        }
        .cpn-col {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .cpn-col-head {
          font-size: 10px;
          font-weight: 700;
          color: #8e8e8e;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          margin-bottom: 4px;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-col-subdesc {
          font-size: 11px;
          color: #b0b0b0;
          margin-bottom: 12px;
          font-family: 'Segoe UI', sans-serif;
          line-height: 1.3;
        }
        .cpn-product-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 10px;
          border-radius: 8px;
          cursor: pointer;
          border: none;
          background: transparent;
          text-align: left;
          width: 100%;
          transition: background 0.12s ease;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-product-row:hover {
          background: #f5f5f5;
        }
        .cpn-product-icon {
          width: 34px;
          height: 34px;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
        }
        .cpn-product-name {
          font-size: 13px;
          font-weight: 600;
          color: #2c2c2c;
          margin-bottom: 1px;
          line-height: 1.2;
          transition: color 0.12s ease;
        }
        .cpn-product-row:hover .cpn-product-name {
          color: #1473e6;
        }
        .cpn-product-subdesc {
          font-size: 11px;
          color: #8e8e8e;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 220px;
        }
        .cpn-cta-col {
          flex-shrink: 0;
          width: 220px;
          border-left: 1px solid #ebebeb;
          margin-left: 24px;
          padding-left: 28px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .cpn-cta-head {
          font-size: 10px;
          font-weight: 700;
          color: #8e8e8e;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          margin-bottom: 8px;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-quick-link {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #1473e6;
          cursor: pointer;
          padding: 5px 0;
          border: none;
          background: none;
          font-family: 'Segoe UI', sans-serif;
          transition: opacity 0.12s ease;
          text-align: left;
        }
        .cpn-quick-link:hover { opacity: 0.7; }
        .cpn-help-box {
          margin-top: auto;
          background: linear-gradient(135deg, #EFF6FF 0%, #E0F2FE 100%);
          border-radius: 10px;
          padding: 16px;
        }
        .cpn-help-title {
          font-size: 12px;
          font-weight: 700;
          color: #1E40AF;
          margin-bottom: 5px;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-help-desc {
          font-size: 11px;
          color: #4b4b4b;
          line-height: 1.5;
          margin-bottom: 12px;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-help-btn {
          width: 100%;
          padding: 8px;
          background: #1473e6;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          font-family: 'Segoe UI', sans-serif;
          transition: background 0.15s ease;
        }
        .cpn-help-btn:hover { background: #0d66d0; }
        .cpn-mega-footer {
          border-top: 1px solid #f0f0f0;
          padding: 14px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fafafa;
          max-width: 100%;
        }
        .cpn-footer-left {
          font-size: 12px;
          color: #8e8e8e;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-footer-right {
          display: flex;
          gap: 24px;
        }
        .cpn-footer-link {
          font-size: 12px;
          color: #1473e6;
          cursor: pointer;
          border: none;
          background: none;
          font-family: 'Segoe UI', sans-serif;
          font-weight: 500;
          transition: opacity 0.12s;
        }
        .cpn-footer-link:hover { opacity: 0.7; }
        .cpn-trigger-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          background: transparent;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #292929;
          cursor: pointer;
          padding: 6px 10px;
          border-radius: 6px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          transition: color 0.15s ease, background 0.15s ease;
          white-space: nowrap;
        }
        .cpn-trigger-btn:hover { background: #f0f0f0; }
        .cpn-trigger-btn.open  { color: #1473e6; background: #EFF6FF; }
        .cpn-trigger-chevron {
          font-size: 13px;
          transition: transform 0.22s cubic-bezier(0.16,1,0.3,1);
        }
        .cpn-trigger-chevron.open { transform: rotate(180deg); }
      `}</style>

      <div ref={ref} style={{ position: "relative" }}>
        {/* ── Trigger ── */}
        <button
          className={`cpn-trigger-btn${open ? " open" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="true"
        >
          Plans &amp; Products
          <ChevronDownRegular className={`cpn-trigger-chevron${open ? " open" : ""}`} />
        </button>
      </div>

      {/* ── Curtain (rendered outside the button div so it's truly full-width) ── */}
      {open && (
        <>
          {/* Dim overlay behind curtain */}
          <div className="cpn-mega-overlay" onClick={() => setOpen(false)} />

          {/* The curtain itself */}
          <div className="cpn-mega-curtain">
            <div className="cpn-mega-inner">

              {/* Three product columns */}
              {COLUMNS.map((col, ci) => {
                const colProducts = products.filter(col.filter);
                return (
                  <React.Fragment key={col.label}>
                    <div className="cpn-col">
                      <div className="cpn-col-head">{col.label}</div>
                      <div className="cpn-col-subdesc">{col.desc}</div>

                      {colProducts.map((p) => {
                        const m = PRODUCT_META[p.name] ?? DEFAULT_META;
                        return (
                          <button
                            key={p.id}
                            className="cpn-product-row"
                            onClick={() => { onProductClick(p); setOpen(false); }}
                          >
                            <div
                              className="cpn-product-icon"
                              style={{ background: m.bg }}
                            >
                              {m.icon}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="cpn-product-name">{p.name}</div>
                              <div className="cpn-product-subdesc">{p.description}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Divider between columns */}
                    {ci < COLUMNS.length - 1 && (
                      <div className="cpn-col-divider" />
                    )}
                  </React.Fragment>
                );
              })}

              {/* CTA column */}
              <div className="cpn-cta-col">
                <div className="cpn-cta-head">Quick links</div>

                {[
                  { label: "View all plans",     action: () => { onViewAll(); setOpen(false); } },
                  { label: "Compare products",   action: () => setOpen(false) },
                  { label: "Enterprise pricing", action: () => setOpen(false) },
                  { label: "Contact sales",      action: () => setOpen(false) },
                  { label: "API documentation",  action: () => setOpen(false) },
                ].map(({ label, action }) => (
                  <button key={label} className="cpn-quick-link" onClick={action}>
                    {label} →
                  </button>
                ))}

                <div className="cpn-help-box">
                  <div className="cpn-help-title">Need help choosing?</div>
                  <div className="cpn-help-desc">
                    Talk to our team and we'll find the right plan for your business.
                  </div>
                  <button className="cpn-help-btn" onClick={() => setOpen(false)}>
                    Talk to sales
                  </button>
                </div>
              </div>
            </div>

            {/* Footer bar */}
            <div className="cpn-mega-footer">
              <span className="cpn-footer-left">
                {products.length} products available · All plans include 24/7 support
              </span>
              <div className="cpn-footer-right">
                <button className="cpn-footer-link" onClick={() => { onViewAll(); setOpen(false); }}>
                  View all plans
                </button>
                <button className="cpn-footer-link" onClick={() => setOpen(false)}>
                  Free trial
                </button>
                <button className="cpn-footer-link" onClick={() => setOpen(false)}>
                  Pricing FAQ
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MegaMenu;